import cx from 'classnames';
import { type KeyboardEvent, useId, useRef } from 'react';
import type { Control, FieldValues, Path } from 'react-hook-form';
import { useController } from 'react-hook-form';
import typography from '~styles/typography.scss';
import styles from './ControlledFormSegmentedControl.scss';

export interface SegmentedControlOption<V extends string> {
  label: string;
  value: V;
}

export interface ControlledFormSegmentedControlProps<
  T extends FieldValues,
  V extends string,
> {
  control: Control<T>;
  name: Path<T>;
  options: SegmentedControlOption<V>[];
  label: string;
  isDisabled?: boolean;
  /** Id of an element that describes the selected option, e.g. a helper line. */
  describedById?: string;
}

const FOCUSABLE_TAB_INDEX_IN_PAGE_TAB_SEQUENCE = 0;
const FOCUSABLE_TAB_INDEX_OUTSIDE_PAGE_TAB_SEQUENCE = -1;

const getArrowStep = (key: string): number => {
  if (key === 'ArrowRight' || key === 'ArrowDown') return 1;
  if (key === 'ArrowLeft' || key === 'ArrowUp') return -1;
  return 0;
};

export const ControlledFormSegmentedControl = <
  T extends FieldValues,
  V extends string,
>({
  control,
  name,
  options,
  label,
  isDisabled,
  describedById,
}: ControlledFormSegmentedControlProps<T, V>) => {
  const {
    field: { onChange, value },
  } = useController({ control, name });

  const labelId = useId();
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const selectedIndex = options.findIndex((option) => option.value === value);
  // Roving tabindex: the group is one Tab stop, on the selected option (or the first when none is).
  const focusableIndex = Math.max(selectedIndex, 0);

  const handleKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
  ): void => {
    const step = getArrowStep(event.key);
    if (isDisabled || step === 0) return;

    event.preventDefault();
    const nextIndex = (index + step + options.length) % options.length;
    onChange(options[nextIndex].value);
    optionRefs.current[nextIndex]?.focus();
  };

  return (
    <div className={styles.fieldContainer}>
      <span className={typography.c2_20} id={labelId}>
        {label}
      </span>
      <div
        aria-describedby={describedById}
        aria-labelledby={labelId}
        className={styles.radioContainer}
        role="radiogroup"
      >
        {options.map((option, index) => (
          <button
            aria-checked={index === selectedIndex}
            className={cx(typography.t2, {
              [styles.isDisabled]: isDisabled,
            })}
            disabled={isDisabled}
            key={option.value}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            ref={(element) => {
              optionRefs.current[index] = element;
            }}
            role="radio"
            tabIndex={
              index === focusableIndex
                ? FOCUSABLE_TAB_INDEX_IN_PAGE_TAB_SEQUENCE
                : FOCUSABLE_TAB_INDEX_OUTSIDE_PAGE_TAB_SEQUENCE
            }
            type="button"
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
};
